import { useState } from 'react';
import { FaEnvelope, FaPhone, FaMapMarkerAlt, FaFacebook, FaTwitter, FaLinkedin, FaInstagram, FaPaperPlane } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';

const Contact = () => {
  const { user, loading } = useAuth();
  const [formData, setFormData] = useState({ 
    name: '', 
    email: '', 
    message: '' 
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Note: PrivateRoute check is likely better placed in the Router, 
  // but keeping logic consistent with previous implementation if intended for this component.
  // However, contact pages are usually public. I will remove the auth check for the contact page 
  // to make it accessible to everyone unless strictly required otherwise.
  // If strict auth was needed, it should be wrapped at the route level.

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setStatus({
      type: 'success',
      message: 'Thank you for reaching out! We will get back to you shortly.',
    });
    setFormData({ name: '', email: '', message: '' });
    setIsSubmitting(false);
    
    // Clear success message after 5 seconds
    setTimeout(() => setStatus({ type: '', message: '' }), 5000);
  };

  if (loading) return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-12">
      <div className="container mx-auto px-4 max-w-7xl">
        
        {/* Header Section */}
        <div className="text-center mb-16 animate-fade-in-down">
          <div className="inline-flex items-center px-4 py-1.5 bg-blue-100 text-blue-700 rounded-full text-sm font-bold uppercase tracking-wider mb-6">
            <FaEnvelope className="w-4 h-4 mr-2" />
            Get in Touch
          </div>
          
          <h1 className="heading-primary mb-6 text-gray-900">
            We'd Love to <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Hear From You</span>
          </h1>
          
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Have a question, feedback, or just want to say hello? We're here to help you on your career journey.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
            
            {/* Contact Info & Map */}
            <div className="space-y-8 animate-slide-in-left">
                
                {/* Info Cards */}
                <div className="grid sm:grid-cols-2 gap-4">
                     <div className="bg-white p-6 rounded-3xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow group">
                        <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center text-xl mb-4 group-hover:scale-110 transition-transform duration-300">
                             <FaEnvelope />
                        </div>
                        <h3 className="font-bold text-gray-900 mb-1">Email Us</h3>
                        <a href="mailto:gaplify@gmail.com" className="text-gray-600 hover:text-blue-600 transition-colors">gaplify@gmail.com</a>
                     </div>

                     <div className="bg-white p-6 rounded-3xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow group">
                        <div className="w-12 h-12 bg-purple-50 text-purple-600 rounded-2xl flex items-center justify-center text-xl mb-4 group-hover:scale-110 transition-transform duration-300">
                             <FaPhone />
                        </div>
                        <h3 className="font-bold text-gray-900 mb-1">Call Us</h3>
                        <a href="tel:+918408868107" className="text-gray-600 hover:text-purple-600 transition-colors">+91 8408868107</a>
                     </div>

                     <div className="bg-white p-6 rounded-3xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow group sm:col-span-2">
                        <div className="flex items-start gap-4">
                             <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center text-xl shrink-0 group-hover:scale-110 transition-transform duration-300">
                                <FaMapMarkerAlt />
                            </div>
                            <div>
                                <h3 className="font-bold text-gray-900 mb-1">Visit Us</h3>
                                <p className="text-gray-600">Pune, Maharashtra, India</p>
                            </div>
                        </div>
                     </div>
                </div>

                {/* Social Links */}
                 <div className="bg-white p-8 rounded-3xl shadow-lg border border-gray-100">
                    <h3 className="text-lg font-bold text-gray-900 mb-6">Follow Our Journey</h3>
                    <div className="flex gap-4">
                        {[
                            { icon: FaInstagram, link: "https://www.instagram.com/gaplify/", color: "hover:bg-pink-500 hover:text-white text-pink-600 bg-pink-50" },
                            { icon: FaFacebook, link: "https://www.facebook.com/search/top?q=gaplify", color: "hover:bg-blue-600 hover:text-white text-blue-600 bg-blue-50" },
                            { icon: FaTwitter, link: "https://x.com/gaplify", color: "hover:bg-sky-500 hover:text-white text-sky-500 bg-sky-50" },
                            { icon: FaLinkedin, link: "https://www.linkedin.com/company/gaplify/", color: "hover:bg-blue-700 hover:text-white text-blue-700 bg-blue-50" }
                        ].map((social, idx) => (
                            <a 
                                key={idx}
                                href={social.link} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl transition-all duration-300 ${social.color}`}
                            >
                                <social.icon />
                            </a>
                        ))}
                    </div>
                </div>

                {/* Map */}
                <div className="h-64 w-full overflow-hidden rounded-3xl shadow-lg border border-gray-100 relative group">
                     <iframe
                        title="Gaplify location"
                        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d242117.70906969727!2d73.69815309340439!3d18.524870610788835!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bc2bf2e67461101%3A0x828d43bf9d9ee343!2sPune%2C%20Maharashtra!5e0!3m2!1sen!2sin!4v1755193231714!5m2!1sen!2sin"
                        width="100%"
                        height="100%"
                        allowFullScreen=""
                        loading="lazy"
                        className="border-0 opacity-90 group-hover:opacity-100 transition-opacity"
                    ></iframe>
                </div>

            </div>

            {/* Contact Form */}
            <div className="bg-white p-8 md:p-10 rounded-[2.5rem] shadow-xl border border-gray-100 animate-slide-in-right relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50 rounded-bl-[4rem] -mr-8 -mt-8 z-0"></div>
                
                <h2 className="heading-tertiary text-gray-900 mb-2 relative z-10">Send us a Message</h2>
                <p className="text-gray-500 mb-8 relative z-10">Fill out the form below and we'll get back to you within 24 hours.</p>
                
                <form onSubmit={handleSubmit} className="space-y-6 relative z-10">
                    {status.message && (
                    <div className={`p-4 rounded-xl text-center text-sm font-semibold animate-fade-in ${status.type === 'success' ? 'bg-green-50 text-green-600 border border-green-100' : 'bg-red-50 text-red-600 border border-red-100'}`}>
                        {status.message}
                    </div>
                    )}
                    
                    <div>
                    <label htmlFor="name" className="block text-sm font-bold text-gray-700 mb-2 ml-1">
                        Full Name
                    </label>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="e.g. John Doe"
                        className="w-full px-5 py-3.5 rounded-xl bg-gray-50 border-transparent focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all outline-none font-medium"
                        required
                    />
                    </div>
                    
                    <div>
                    <label htmlFor="email" className="block text-sm font-bold text-gray-700 mb-2 ml-1">
                        Email Address
                    </label>
                    <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="e.g. john@example.com"
                        className="w-full px-5 py-3.5 rounded-xl bg-gray-50 border-transparent focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all outline-none font-medium"
                        required
                    />
                    </div>
                    
                    <div>
                    <label htmlFor="message" className="block text-sm font-bold text-gray-700 mb-2 ml-1">
                        Your Message
                    </label>
                    <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows="5"
                        placeholder="How can we help you?"
                        className="w-full px-5 py-3.5 rounded-xl bg-gray-50 border-transparent focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all outline-none font-medium resize-none"
                        required
                    ></textarea>
                    </div>
                    
                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full py-4 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold shadow-lg shadow-blue-500/30 hover:shadow-xl hover:shadow-blue-500/40 transform hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                        {isSubmitting ? (
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        ) : (
                            <>
                                <FaPaperPlane className="text-sm" /> Send Message
                            </>
                        )}
                    </button>
                    
                    <p className="text-xs text-center text-gray-400 mt-4">
                        By sending this message, you agree to our privacy policy.
                    </p>
                </form>
            </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;